// Current Openings Block — Wayflyer redesign (Figma 5282-17943 desktop / 5628-21804 mobile).
// A filterable job board on a grey-50 surface. Desktop: a left filter sidebar
// (Departments + Location Type) beside a bordered list whose hovered row fills
// with the blog listing gradient and swaps its arrow for an "Apply now" pill.
// Mobile: a horizontally-scrolling chip row above stacked rows with a "+" button.

"use client";

import { ArrowRight, Plus } from "iconoir-react";
import * as React from "react";

import { SectionRevealHeading } from "@/components/blocks/section-reveal-heading";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "@/components/ui/link";
import { SPECULATIVE_APPLICATION_URL } from "@/content/shared/careers/openings";
import { cn } from "@/lib/utils";
import type { JobOpening, WorkplaceType } from "@/schemas/job-opening";

// Gradient hover uses a child overlay — see `.open-roles-row-hover-bg` in globals.css.

/** Shared underline treatment for the two links beneath the job board. */
const SECONDARY_LINK_CLASS =
  "text-grey-950 underline underline-offset-4 transition-opacity duration-200 ease-out hover:opacity-70";

export interface CurrentOpeningsLabels {
  heading: string;
  filters: string;
  clearAll: string;
  departments: string;
  locationType: string;
  allDepartments: string;
  applyNow: string;
  empty: string;
  noOpenings: string;
  /** US-only (en) recruiting-event prompt; omitted on every other locale. */
  speculativePrompt?: string;
  speculativeCtaLabel?: string;
  candidateHubPrompt: string;
  candidateHubCtaLabel: string;
}

export interface Facet<T extends string = string> {
  value: T;
  count: number;
}

export interface CurrentOpeningsProps {
  jobs: JobOpening[];
  labels: CurrentOpeningsLabels;
  /** Department filter facets — label, order, and count come from the caller
   * (display values; not derived from `jobs`). */
  departmentFacets: Facet[];
  /** Location-type filter facets, in display order. */
  workplaceFacets: Facet<WorkplaceType>[];
  /** Count shown beside the "All Departments" option. */
  allDepartmentsCount: number;
  /** Optional centered band heading above the listing. */
  title?: string;
  id?: string;
  className?: string;
}

function toggleInSet<T extends string>(
  setter: React.Dispatch<React.SetStateAction<Set<T>>>,
  value: T
) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  });
}

function CurrentOpenings({
  jobs,
  labels,
  departmentFacets,
  workplaceFacets,
  allDepartmentsCount,
  title,
  id,
  className,
}: CurrentOpeningsProps) {
  const [selectedDepartments, setSelectedDepartments] = React.useState<
    Set<string>
  >(new Set());
  const [selectedWorkplaces, setSelectedWorkplaces] = React.useState<
    Set<WorkplaceType>
  >(new Set());

  const filtered = React.useMemo(
    () =>
      jobs.filter(
        (job) =>
          (selectedDepartments.size === 0 ||
            selectedDepartments.has(job.department)) &&
          (selectedWorkplaces.size === 0 ||
            selectedWorkplaces.has(job.workplaceType))
      ),
    [jobs, selectedDepartments, selectedWorkplaces]
  );

  const clearAll = () => {
    setSelectedDepartments(new Set());
    setSelectedWorkplaces(new Set());
  };

  const allDepartmentsSelected = selectedDepartments.size === 0;
  const hasActiveFilters =
    selectedDepartments.size > 0 || selectedWorkplaces.size > 0;

  // Nothing to clear while "All Departments" is selected and no location type
  // is checked, matching the blog and press-room filter rails.
  const clearButton = (
    <button
      type="button"
      onClick={clearAll}
      disabled={!hasActiveFilters}
      className={cn(
        "text-ds-body-xs transition-colors",
        hasActiveFilters
          ? "text-grey-600 hover:text-grey-950 cursor-pointer"
          : "text-grey-300 cursor-not-allowed"
      )}
    >
      {labels.clearAll}
    </button>
  );

  return (
    <section
      id={id}
      className={cn(
        "bg-grey-50 flex scroll-mt-[calc(var(--nav-offset)+16px)] flex-col items-center gap-8 pt-8 pb-4 lg:gap-10 lg:px-20 lg:pt-20 lg:pb-10",
        className
      )}
    >
      {title ? (
        <SectionRevealHeading className="px-6 lg:px-0">
          {title}
        </SectionRevealHeading>
      ) : null}

      <div className="mx-auto w-full max-w-[1280px]">
        {/* Mobile: filters header + horizontally-scrolling chips (Figma 5628-21804). */}
        <div className="bg-grey-0 lg:hidden">
          <div className="flex h-[49px] items-center justify-between px-6">
            <span className="text-ds-body-xs text-grey-950">
              {labels.filters}
            </span>
            {clearButton}
          </div>
          <div className="relative h-[72px] overflow-hidden">
            <div className="flex h-full items-center gap-2 overflow-x-auto px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <FilterChip
                label={`${labels.allDepartments} (${allDepartmentsCount})`}
                checked={allDepartmentsSelected}
                onToggle={() => setSelectedDepartments(new Set())}
              />
              {departmentFacets.map((d) => (
                <FilterChip
                  key={d.value}
                  label={`${d.value} (${d.count})`}
                  checked={selectedDepartments.has(d.value)}
                  onToggle={() => toggleInSet(setSelectedDepartments, d.value)}
                />
              ))}
              {workplaceFacets.map((w) => (
                <FilterChip
                  key={w.value}
                  label={`${w.value} (${w.count})`}
                  checked={selectedWorkplaces.has(w.value)}
                  onToggle={() => toggleInSet(setSelectedWorkplaces, w.value)}
                />
              ))}
            </div>
            <div
              aria-hidden
              className="from-grey-0 pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l to-transparent"
            />
          </div>
        </div>

        <div className="bg-grey-0 border-grey-100 grid grid-cols-1 overflow-hidden lg:h-[912px] lg:grid-cols-[217px_minmax(0,1fr)] lg:border">
          {/* Desktop: filter sidebar — 217px rail matches blog / Figma 5282-17943. */}
          <aside className="border-grey-100 bg-grey-0 hidden min-h-0 flex-col overflow-y-auto border-r lg:flex">
            <div className="border-grey-100 flex h-[49px] items-end justify-between gap-2 border-b px-4 py-4">
              <span className="text-ds-body-xs text-grey-950">
                {labels.heading}
              </span>
              {clearButton}
            </div>

            <FilterGroup label={labels.departments}>
              <FilterItem
                label={`${labels.allDepartments} (${allDepartmentsCount})`}
                checked={allDepartmentsSelected}
                onToggle={() => setSelectedDepartments(new Set())}
              />
              {departmentFacets.map((d) => (
                <FilterItem
                  key={d.value}
                  label={`${d.value} (${d.count})`}
                  checked={selectedDepartments.has(d.value)}
                  onToggle={() => toggleInSet(setSelectedDepartments, d.value)}
                />
              ))}
            </FilterGroup>

            <FilterGroup label={labels.locationType}>
              {workplaceFacets.map((w) => (
                <FilterItem
                  key={w.value}
                  label={`${w.value} (${w.count})`}
                  checked={selectedWorkplaces.has(w.value)}
                  onToggle={() => toggleInSet(setSelectedWorkplaces, w.value)}
                />
              ))}
            </FilterGroup>
          </aside>

          {/* Job list — fixed 912px board on desktop; scroll inside the rows column. */}
          <div className="border-grey-100 bg-grey-0 flex min-h-0 flex-col overflow-y-auto border-t lg:border-0 lg:[scrollbar-gutter:stable]">
            {filtered.length === 0 ? (
              <p className="text-ds-body-sm text-grey-500 flex flex-1 items-center justify-center px-6 py-10 text-center lg:px-10">
                {jobs.length === 0 ? labels.noOpenings : labels.empty}
              </p>
            ) : (
              filtered.map((job, i) => (
                <JobRow
                  key={`${job.title}-${i}`}
                  job={job}
                  applyLabel={labels.applyNow}
                  showBottomBorder={
                    i < filtered.length - 1 ||
                    hasActiveFilters ||
                    filtered.length < jobs.length
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Secondary routes out of the job board: interview prep for applicants
          who already applied, and (en only) the speculative form. */}
      {/* Wraps rather than breakpoint-switching: on en both sit side by side
          and drop to a stack when the row won't fit; every other locale
          renders a single centred line. */}
      <div className="text-ds-body-sm text-grey-500 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 px-6 text-center lg:px-0">
        <p>
          {labels.candidateHubPrompt}{" "}
          <Link href="/candidate-success-hub" className={SECONDARY_LINK_CLASS}>
            {labels.candidateHubCtaLabel}
          </Link>
        </p>

        {labels.speculativePrompt && labels.speculativeCtaLabel ? (
          <p>
            {labels.speculativePrompt}{" "}
            <a
              href={SPECULATIVE_APPLICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={SECONDARY_LINK_CLASS}
            >
              {labels.speculativeCtaLabel}
            </a>
          </p>
        ) : null}
      </div>
    </section>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    // The rule sits above the group (Figma 5282-17943) — it doubles as the
    // heading row's underline for the first group and separates later ones.
    <div className="border-grey-100 border-t">
      <div className="px-4 py-2.5">
        <span className="text-ds-body-xs text-grey-950">{label}</span>
      </div>
      <div className="flex flex-col gap-2 p-3">{children}</div>
    </div>
  );
}

function FilterItem({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="rounded-ds-m hover:bg-grey-50 flex min-h-8 cursor-pointer items-center gap-2 px-2 py-2 transition-colors">
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        className="shrink-0"
      />
      <span className="text-ds-label-sm text-grey-950 min-w-0">{label}</span>
    </label>
  );
}

function FilterChip({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={cn(
        "rounded-ds-m flex h-8 shrink-0 cursor-pointer items-center gap-2 px-2 py-2 transition-colors",
        checked ? "bg-grey-50" : "bg-grey-0"
      )}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="text-ds-label-sm text-grey-950 whitespace-nowrap">
        {label}
      </span>
    </label>
  );
}

function JobRow({
  job,
  applyLabel,
  showBottomBorder = true,
}: {
  job: JobOpening;
  applyLabel: string;
  showBottomBorder?: boolean;
}) {
  return (
    <a
      href={job.jobUrl ?? job.applyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "border-grey-100 group/row relative flex items-center gap-6 px-6 py-5 transition-colors duration-200 ease-out lg:gap-14 lg:px-10 lg:py-6",
        showBottomBorder && "border-b"
      )}
    >
      <span aria-hidden className="open-roles-row-hover-bg" />
      <span className="relative flex min-w-0 flex-1 flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
        <span className="text-ds-body-xs text-grey-500 group-hover/row:text-grey-200 whitespace-nowrap transition-colors duration-200 ease-out">
          {job.employmentType} · {job.locationLabel}
        </span>
        <span
          aria-hidden
          className="bg-grey-100 hidden h-5 w-px shrink-0 transition-colors duration-200 ease-out group-hover/row:bg-white/20 lg:block"
        />
        <span className="text-ds-body-sm text-grey-950 group-hover/row:text-grey-0 min-w-0 transition-colors duration-200 ease-out">
          {job.title}
        </span>
      </span>

      <span className="relative size-7 shrink-0">
        {/* Default circle: arrow on desktop, plus on mobile. Fades out on desktop hover. */}
        <span className="border-grey-100 bg-grey-0 text-grey-950 rounded-ds-full group-hover/row:text-grey-0 absolute inset-0 flex items-center justify-center border transition-[opacity,background-color,color] duration-200 ease-out group-hover/row:bg-white/15 motion-reduce:transition-none lg:group-hover/row:opacity-0">
          <ArrowRight className="hidden size-3 lg:block" />
          <Plus className="size-3 lg:hidden" />
        </span>
        {/* Apply now pill — desktop hover only. */}
        <span className="border-grey-100 bg-grey-0 text-grey-950 rounded-ds-full group-hover/row:text-grey-0 absolute inset-y-0 right-0 hidden min-w-7 items-center justify-center border px-3 py-1 opacity-0 transition-[opacity,background-color,color] duration-200 ease-out group-hover/row:bg-white/15 group-hover/row:opacity-100 motion-reduce:transition-none lg:flex">
          <span className="text-ds-label-md font-medium whitespace-nowrap">
            {applyLabel}
          </span>
        </span>
      </span>
    </a>
  );
}

export { CurrentOpenings };
